/**
 * <image-gallery> — the lightbox half of sections/image-gallery.liquid.
 *
 * The grid itself needs no JavaScript at all (masonry is CSS multi-column,
 * see assets/section-image-gallery.css). This file only exists to open the
 * <dialog>, step through the slides, and put the caret back where it came
 * from — so with the script blocked the gallery is still a gallery, just
 * without the enlarge.
 *
 * Built on window.ThemeOverlay (assets/overlay-motion.js) like every other
 * overlay in the theme: it owns the open/close motion, the stack-aware
 * scroll lock and focus restore. Every call is guarded, so a missing
 * ThemeOverlay degrades to a plain showModal()/close() rather than to a
 * dead button.
 *
 * Esc is intercepted at `cancel` rather than left to the browser. A native
 * Esc closes the dialog on the spot, which skips the exit animation AND
 * skips the unlockScroll that only runs on our own close path — leaving the
 * page scroll-locked with nothing on screen to explain it. The cart drawer
 * and the product gallery both do this; it is the standard trap here.
 */
if (!customElements.get('image-gallery')) {
  /* Below this, a horizontal drag is a tap or a scroll, not a swipe. */
  var SWIPE_THRESHOLD_PX = 40;

  class ImageGallery extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-gallery-lightbox]');
      this.stage = this.querySelector('[data-gallery-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      if (!this.dialog || !this.slides.length) return;

      this.index = 0;
      this.trigger = null;
      this.pointerStartX = null;

      this.onClick = this.onClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClosed = this.onClosed.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.addEventListener('click', this.onClick);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClosed);

      if (this.stage && window.PointerEvent) {
        this.stage.addEventListener('pointerdown', this.onPointerDown);
        this.stage.addEventListener('pointerup', this.onPointerUp);
        /* A drag that leaves the stage never fires pointerup on it, so the
           start position would survive into the next gesture and turn an
           unrelated tap into a swipe. */
        this.stage.addEventListener('pointercancel', () => {
          this.pointerStartX = null;
        });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (!this.dialog) return;
      this.dialog.removeEventListener('click', this.onDialogClick);
      this.dialog.removeEventListener('keydown', this.onKeydown);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClosed);
      if (window.ThemeOverlay) {
        window.ThemeOverlay.cancelClose(this.dialog);
        /* The theme editor removes and re-renders a section on every
           setting change. A gallery removed with its lightbox open would
           otherwise take the scroll lock with it. */
        window.ThemeOverlay.unlockScroll(this);
      }
    }

    onClick(event) {
      var opener = event.target.closest('[data-gallery-open]');
      if (opener) {
        this.open(Number(opener.dataset.galleryOpen) || 0, opener);
        return;
      }
      if (event.target.closest('[data-gallery-close]')) {
        this.close();
        return;
      }
      if (event.target.closest('[data-gallery-prev]')) {
        this.step(-1);
        return;
      }
      if (event.target.closest('[data-gallery-next]')) this.step(1);
    }

    /* A click landing on the <dialog> element itself, rather than on the
       panel filling it, is a backdrop click. */
    onDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
        return;
      }
      /* Belt and braces over the top layer's native trap: a modal <dialog>
         already confines Tab, and this keeps the behaviour identical to the
         theme's non-dialog overlays, which have no native trap at all. */
      if (window.ThemeOverlay) window.ThemeOverlay.trapTab(event, this.dialog);
    }

    onPointerDown(event) {
      this.pointerStartX = event.clientX;
    }

    onPointerUp(event) {
      if (this.pointerStartX === null) return;
      var delta = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
      /* Swiping left (a negative delta) pulls the next image in from the
         right, which is the direction the content moves, not the finger. */
      this.step(delta < 0 ? 1 : -1);
    }

    open(index, trigger) {
      if (typeof this.dialog.showModal !== 'function') return;
      this.trigger = trigger || null;
      this.setIndex(index);
      if (window.ThemeOverlay) {
        window.ThemeOverlay.cancelClose(this.dialog);
        window.ThemeOverlay.openDialog(this.dialog);
        /* showModal() makes the page inert but does not stop it scrolling. */
        window.ThemeOverlay.lockScroll(this);
      } else {
        this.dialog.showModal();
        this.dialog.classList.add('is-visible');
      }
    }

    close() {
      if (!this.dialog.open) return;
      if (window.ThemeOverlay) {
        window.ThemeOverlay.closeDialog(this.dialog);
      } else {
        this.dialog.close();
      }
    }

    onCancel(event) {
      event.preventDefault();
      this.close();
    }

    onClosed() {
      this.dialog.classList.remove('is-visible');
      if (!window.ThemeOverlay) return;
      window.ThemeOverlay.cancelClose(this.dialog);
      window.ThemeOverlay.unlockScroll(this);
      window.ThemeOverlay.restoreFocus(this.trigger);
      this.trigger = null;
    }

    /* Wraps rather than clamping, matching the product gallery and the hero
       carousel — a gallery that dead-ends is the odd one out in this theme.
       Modulo twice, because JavaScript's % keeps the sign of a negative. */
    step(delta) {
      if (this.slides.length < 2) return;
      var total = this.slides.length;
      this.setIndex((((this.index + delta) % total) + total) % total);
    }

    setIndex(index) {
      if (index < 0 || index >= this.slides.length) index = 0;
      this.index = index;
      this.slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      if (!this.counter) return;
      var template = this.counter.dataset.galleryCounterTemplate || '';
      this.counter.textContent = template
        .replace('{{index}}', String(index + 1))
        .replace('{{total}}', String(this.slides.length));
    }
  }

  customElements.define('image-gallery', ImageGallery);
}
